// apps/client/src/lib/queryClient.ts
// Singleton QueryClient for dashboard data caching (Tier 3 loading UX).
//
// Global defaults are deliberately conservative for a BigQuery-backed app:
// every refetch is a real BigQuery scan with real cost, so nothing refetches
// on window focus or reconnect. Staleness/caching semantics are NOT set here;
// they are decided per-tenant by the `dashboard_query_cache` feature flag in
// useDashboardQuery.ts (fail-safe default: no caching, byte-identical to the
// pre-react-query behavior).
import { QueryClient } from '@tanstack/react-query';

/**
 * Every dashboard query key starts with this prefix — aggregation-complete
 * invalidation (useQueryCacheLifecycle) targets it. Lives here rather than in
 * useDashboardQuery so cache-topology consumers don't import the Firebase
 * initialization that hook pulls in (keeps them unit-testable).
 */
export const DASHBOARD_QUERY_PREFIX = 'dashboard';

/**
 * Per-tenant cache rollout mode (companies/{id}.featureFlags.dashboard_query_cache).
 * House tri-state convention: fail-safe default 'legacy', 'observe' soak rung,
 * 'swr' fully on. Semantics documented in useDashboardQuery.ts.
 */
export type DashboardCacheMode = 'legacy' | 'observe' | 'swr';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // BigQuery callables are expensive — never refetch on focus/reconnect.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // The bespoke hooks this replaces never retried — keep that behavior.
      // (A retry here would double BigQuery spend during any systematic
      // failure, fleet-wide. Resilience changes are their own task.)
      retry: false,
    },
  },
});
